import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/dialog';
import { Button } from '@shared/components/ui/button';
import { Label } from '@shared/components/ui/label';
import { Input } from '@shared/components/ui/input';
import { UserPlus, Loader2, CheckCircle2, XCircle, Trash2, Users, ArrowRight } from 'lucide-react';
import { getPortalToken } from '@shared/services/portalService';

const EMPTY_FORM = {
  person_name: '',
  person_email: '',
  person_phone: '',
  person_company: '',
  person_role: '',
};

// Phases:
//   'build'   – user is staging members into the list
//   'sending' – batch is being sent to the edge function
//   'done'    – all done, show results

// onSuccess  — called after each successful invite (to reload list etc.)
// onDone     — called when the user dismisses after completion
// skipDonePhase — when true, skips the results screen and calls onDone immediately
export function InviteTeamMemberDialog({ open, onOpenChange, projectId, onSuccess, onDone, skipDonePhase = false }) {
  const [formData, setFormData] = React.useState(EMPTY_FORM);
  const [formError, setFormError] = React.useState('');
  const [pendingMembers, setPendingMembers] = React.useState([]); // staged, not yet sent
  const [phase, setPhase] = React.useState('build'); // 'build' | 'sending' | 'done'
  const [sendResults, setSendResults] = React.useState([]); // { person_name, person_email, ok, error }
  const [sendingIndex, setSendingIndex] = React.useState(0);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormError('');
  };

  // Add the current form entry to the pending list (does NOT call the API yet)
  const handleAddToList = () => {
    const name = formData.person_name.trim();
    const email = formData.person_email.trim().toLowerCase();

    if (!name || !email) {
      setFormError('Name and email are required');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setFormError('Invalid email format');
      return;
    }
    if (pendingMembers.some((m) => m.person_email === email)) {
      setFormError('This email is already in your list');
      return;
    }

    setPendingMembers((prev) => [
      ...prev,
      { ...formData, person_email: email, _id: Date.now() },
    ]);
    setFormData(EMPTY_FORM);
    setFormError('');
  };

  const handleRemovePending = (id) => {
    setPendingMembers((prev) => prev.filter((m) => m._id !== id));
  };

  // Send all staged members as a batch, then trigger a single notification check
  const handleSendAll = async () => {
    if (pendingMembers.length === 0) return;

    setPhase('sending');
    setSendingIndex(0);
    const results = [];
    const portalToken = getPortalToken();

    if (!portalToken) {
      setPhase('done');
      setSendResults(pendingMembers.map((m) => ({
        person_name: m.person_name,
        person_email: m.person_email,
        ok: false,
        error: 'Not authenticated',
      })));
      return;
    }

    const succeeded = [];

    for (let i = 0; i < pendingMembers.length; i++) {
      setSendingIndex(i);
      const member = pendingMembers[i];
      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/project-invite-team`,
          {
            method: 'POST',
            headers: {
              'x-portal-token': portalToken,
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              project_id: projectId,
              person_name: member.person_name,
              person_email: member.person_email,
              person_phone: member.person_phone,
              person_company: member.person_company,
              person_role: member.person_role,
              skip_notification: true, // suppress per-invite check — we trigger once at the end
            }),
          }
        );

        const result = await response.json();
        if (!response.ok) {
          results.push({ person_name: member.person_name, person_email: member.person_email, ok: false, error: result.error || 'Invitation failed' });
        } else {
          results.push({ person_name: member.person_name, person_email: member.person_email, ok: true });
          succeeded.push(result.team_member);
          if (onSuccess) onSuccess(result.team_member);
        }
      } catch (err) {
        results.push({ person_name: member.person_name, person_email: member.person_email, ok: false, error: err.message || 'Network error' });
      }
    }

    // After all invites, trigger ONE notification check.
    // This avoids the race where adding a single already-inducted member
    // would prematurely fire the "all done" email to the lead.
    if (succeeded.length > 0) {
      try {
        await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-team-inducted`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ project_id: projectId }),
          }
        );
      } catch {
        console.warn('notify-team-inducted post-batch call failed');
      }
    }

    setSendResults(results);
    if (skipDonePhase) {
      // Skip the results screen — close and hand back immediately
      setPendingMembers([]);
      setFormData(EMPTY_FORM);
      setFormError('');
      setPhase('build');
      setSendResults([]);
      setSendingIndex(0);
      onOpenChange(false);
      if (onDone) onDone();
    } else {
      setPhase('done');
    }
  };

  const handleClose = () => {
    setPendingMembers([]);
    setFormData(EMPTY_FORM);
    setFormError('');
    setPhase('build');
    setSendResults([]);
    setSendingIndex(0);
    onOpenChange(false);
  };

  const handleDone = () => {
    handleClose();
    if (onDone) onDone();
  };

  const okCount = sendResults.filter((r) => r.ok).length;
  const failCount = sendResults.filter((r) => !r.ok).length;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); else onOpenChange(true); }}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">

        {/* ── DONE phase ── */}
        {phase === 'done' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Invitations Sent
              </DialogTitle>
              <DialogDescription>
                {okCount} invitation{okCount !== 1 ? 's' : ''} sent successfully.
                {failCount > 0 && ` ${failCount} failed.`}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2 py-2">
              {sendResults.map((r, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm ${
                    r.ok ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                  }`}
                >
                  {r.ok
                    ? <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                    : <XCircle className="h-4 w-4 text-red-500 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{r.person_name}</span>
                    <span className="text-muted-foreground ml-2 text-xs truncate">{r.person_email}</span>
                  </div>
                  {!r.ok && <span className="text-red-600 text-xs shrink-0">{r.error}</span>}
                </div>
              ))}
            </div>

            <DialogFooter>
              <Button onClick={handleDone}>
                {onDone ? 'Return to Setup Guide' : 'Done'}
                <ArrowRight className="ml-2 h-3.5 w-3.5" />
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ── SENDING phase ── */}
        {phase === 'sending' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                Sending Invitations…
              </DialogTitle>
              <DialogDescription>
                Sending {sendingIndex + 1} of {pendingMembers.length} — please wait.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2 py-2">
              {pendingMembers.map((m, i) => (
                <div key={m._id} className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm">
                  {i < sendingIndex
                    ? <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                    : i === sendingIndex
                      ? <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
                      : <div className="h-4 w-4 rounded-full border-2 border-muted shrink-0" />}
                  <span className="font-medium">{m.person_name}</span>
                  <span className="text-muted-foreground text-xs truncate">{m.person_email}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── BUILD phase ── */}
        {phase === 'build' && (
          <>
            <DialogHeader>
              <DialogTitle>Invite Team Members</DialogTitle>
              <DialogDescription>
                Add everyone to the list first, then send all invitations at once.
              </DialogDescription>
            </DialogHeader>

            {/* Form */}
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1.5">
                  <Label htmlFor="person_name">Full Name <span className="text-destructive">*</span></Label>
                  <Input
                    id="person_name"
                    placeholder="John Smith"
                    value={formData.person_name}
                    onChange={(e) => handleChange('person_name', e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddToList()}
                  />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label htmlFor="person_email">Email Address <span className="text-destructive">*</span></Label>
                  <Input
                    id="person_email"
                    type="email"
                    placeholder="john.smith@company.com"
                    value={formData.person_email}
                    onChange={(e) => handleChange('person_email', e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddToList()}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="person_phone">Phone</Label>
                  <Input
                    id="person_phone"
                    type="tel"
                    placeholder="07123 456789"
                    value={formData.person_phone}
                    onChange={(e) => handleChange('person_phone', e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="person_company">Company</Label>
                  <Input
                    id="person_company"
                    placeholder="ABC Construction"
                    value={formData.person_company}
                    onChange={(e) => handleChange('person_company', e.target.value)}
                  />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label htmlFor="person_role">Role on Project</Label>
                  <Input
                    id="person_role"
                    placeholder="e.g. Site Supervisor, Operative"
                    value={formData.person_role}
                    onChange={(e) => handleChange('person_role', e.target.value)}
                  />
                </div>
              </div>

              {formError && (
                <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-2.5">
                  {formError}
                </div>
              )}
            </div>

            {/* Pending list */}
            {pendingMembers.length > 0 && (
              <div className="space-y-2 border-t pt-3">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Users className="h-4 w-4" />
                  Ready to invite ({pendingMembers.length})
                </div>
                {pendingMembers.map((m) => (
                  <div key={m._id} className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2 text-sm">
                    <div className="flex-1 min-w-0">
                      <span className="font-medium">{m.person_name}</span>
                      <span className="text-muted-foreground ml-2 text-xs truncate">{m.person_email}</span>
                    </div>
                    <button
                      onClick={() => handleRemovePending(m._id)}
                      className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                      aria-label="Remove"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <DialogFooter className="gap-2 pt-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={handleAddToList}
                disabled={!formData.person_name || !formData.person_email}
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Add to List
              </Button>
              <Button
                onClick={handleSendAll}
                disabled={pendingMembers.length === 0}
              >
                <ArrowRight className="mr-2 h-4 w-4" />
                Send {pendingMembers.length > 0 ? `${pendingMembers.length} ` : ''}Invitation{pendingMembers.length !== 1 ? 's' : ''}
              </Button>
            </DialogFooter>
          </>
        )}

      </DialogContent>
    </Dialog>
  );
}
